import { useState } from 'react';
import { Calendar, MapPin, Users, Star, Clock, Heart, Leaf, Mountain, Waves, Sun } from 'lucide-react';
import { SEO } from '../../SEO';

const WellnessRetreats = () => {
  const [selectedRetreat, setSelectedRetreat] = useState<any>(null);

  const retreats = [
    {
      id: 1,
      title: "Himalayan Serenity Retreat",
      location: "Rishikesh, Uttarakhand",
      duration: "7 Days",
      price: "₹45,000",
      originalPrice: "₹55,000",
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      rating: 4.9,
      reviews: 127,
      category: "Mountain",
      icon: <Mountain className="w-5 h-5" />,
      highlights: ["Daily Yoga Sessions", "Meditation Workshops", "Ayurvedic Meals", "Nature Walks", "Spiritual Guidance"],
      description: "Immerse yourself in the tranquil beauty of the Himalayas while reconnecting with your inner self through ancient practices.",
      includes: ["Accommodation", "All Meals", "Yoga Classes", "Meditation Sessions", "Spa Treatments"]
    },
    {
      id: 2,
      title: "Coastal Wellness Escape",
      location: "Goa Beaches",
      duration: "5 Days",
      price: "₹32,000",
      originalPrice: "₹38,000",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      rating: 4.8,
      reviews: 89,
      category: "Beach",
      icon: <Waves className="w-5 h-5" />,
      highlights: ["Beach Yoga", "Detox Programs", "Organic Cuisine", "Water Therapy", "Sunset Meditation"],
      description: "Experience ultimate relaxation with ocean views, beach yoga sessions, and holistic healing therapies.",
      includes: ["Beachfront Stay", "Organic Meals", "Water Activities", "Spa Sessions", "Wellness Consultations"]
    },
    {
      id: 3,
      title: "Desert Mindfulness Journey",
      location: "Jaisalmer, Rajasthan",
      duration: "4 Days",
      price: "₹28,000",
      originalPrice: "₹34,000",
      image: "https://images.unsplash.com/photo-1545558014-8692077e9b5c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      rating: 4.7,
      reviews: 64,
      category: "Desert",
      icon: <Sun className="w-5 h-5" />,
      highlights: ["Starlit Meditation", "Camel Safari", "Traditional Healing", "Cultural Immersion", "Digital Detox"],
      description: "Find clarity and peace in the vast desert landscape while experiencing traditional Rajasthani wellness practices.",
      includes: ["Desert Camp Stay", "Cultural Activities", "Traditional Meals", "Wellness Sessions", "Transportation"]
    },
    {
      id: 4,
      title: "Forest Therapy Retreat",
      location: "Kerala Backwaters",
      duration: "6 Days",
      price: "₹38,000",
      originalPrice: "₹46,000",
      image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      rating: 4.9,
      reviews: 156,
      category: "Forest",
      icon: <Leaf className="w-5 h-5" />,
      highlights: ["Ayurvedic Treatments", "Forest Bathing", "Herbal Medicine", "Backwater Cruise", "Yoga Nidra"],
      description: "Reconnect with nature through ancient Ayurvedic practices in the lush green forests of Kerala.",
      includes: ["Houseboat Stay", "Ayurvedic Meals", "Spa Treatments", "Nature Activities", "Medical Consultation"]
    }
  ];

  const upcomingDates = [
    { retreat: "Himalayan Serenity", dates: "Mar 15-22, 2024", spots: 8 },
    { retreat: "Coastal Wellness", dates: "Apr 10-15, 2024", spots: 12 },
    { retreat: "Desert Mindfulness", dates: "May 5-9, 2024", spots: 6 },
    { retreat: "Forest Therapy", dates: "Jun 20-26, 2024", spots: 10 }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Transformative Holistic Wellness Retreats Across India | MetaFit Wellness"
        description="Escape into serene nature with curated wellness retreats by MetaFit Wellness. Experience rejuvenating yoga sessions, meditation workshops, and Ayurvedic healing in Rishikesh, Goa, and beyond."
        canonical="/wellness-retreats"
        keywords="wellness retreats India, yoga retreat, meditation escape, Ayurvedic retreat Rishikesh, coastal wellness Goa, MetaFit Wellness"
      />

      {/* Hero Section */}
      <div className="relative h-96 bg-gradient-to-r from-teal-500 to-teal-600">
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-center w-full">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Wellness Retreats
            </h1>
            <p className="text-xl text-white mb-8 max-w-3xl mx-auto">
              Escape the ordinary and embark on transformative wellness journeys designed to rejuvenate your mind, body, and spirit in India's most serene locations.
            </p>
            <div className="flex justify-center space-x-4">
              <button className="bg-white text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                Book Your Retreat
              </button>
              <button className="border border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-teal-600 transition-colors">
                View Calendar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-teal-500 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-white mb-2">200+</div>
              <div className="text-teal-100">Retreats Completed</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-2">95%</div>
              <div className="text-teal-100">Satisfaction Rate</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-2">15+</div>
              <div className="text-teal-100">Locations</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-2">24/7</div>
              <div className="text-teal-100">Support Available</div>
            </div>
          </div>
        </div>
      </div>

      {/* Retreats Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Signature Retreats</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choose from our carefully curated wellness retreats, each designed to provide unique experiences in breathtaking locations across India.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {retreats.map((retreat) => (
            <div key={retreat.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="relative">
                <img src={retreat.image} alt={retreat.title} className="w-full h-64 object-cover" />
                <div className="absolute top-4 left-4 flex items-center space-x-2">
                  <div className="bg-teal-500 text-white px-3 py-1 rounded-full text-sm flex items-center space-x-1">
                    {retreat.icon}
                    <span>{retreat.category}</span>
                  </div>
                </div>
                <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full">
                  <div className="flex items-center space-x-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <span className="text-sm font-medium">{retreat.rating}</span>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{retreat.title}</h3>
                <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                  <div className="flex items-center space-x-1">
                    <MapPin className="w-4 h-4" />
                    <span>{retreat.location}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>{retreat.duration}</span>
                  </div>
                </div>
                
                <p className="text-gray-600 mb-4">{retreat.description}</p>
                
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Highlights:</h4>
                  <div className="flex flex-wrap gap-2">
                    {retreat.highlights.slice(0, 3).map((highlight, index) => (
                      <span key={index} className="bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-sm">
                        {highlight}
                      </span>
                    ))}
                    {retreat.highlights.length > 3 && (
                      <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm">
                        +{retreat.highlights.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl font-bold text-teal-600">{retreat.price}</span>
                      <span className="text-lg text-gray-400 line-through">{retreat.originalPrice}</span>
                    </div>
                    <div className="text-sm text-gray-600">{retreat.reviews} reviews</div>
                  </div>
                  <button 
                    onClick={() => setSelectedRetreat(retreat)}
                    className="bg-teal-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-teal-600 transition-colors"
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Upcoming Dates */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Upcoming Retreat Dates</h2>
            <p className="text-lg text-gray-600">Secure your spot in our upcoming wellness retreats</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {upcomingDates.map((date, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6 text-center">
                <Calendar className="w-8 h-8 text-teal-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">{date.retreat}</h3>
                <p className="text-teal-600 font-medium mb-2">{date.dates}</p>
                <p className="text-sm text-gray-600 mb-3">{date.spots} spots remaining</p>
                <button className="w-full bg-teal-500 text-white py-2 rounded-lg font-medium hover:bg-teal-600 transition-colors">
                  Reserve Now
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* What's Included */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What's Included in Every Retreat</h2>
            <p className="text-lg text-gray-600">Experience comprehensive wellness with our all-inclusive packages</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Expert Guidance</h3>
              <p className="text-gray-600">Certified wellness instructors and therapists to guide your journey</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Holistic Wellness</h3>
              <p className="text-gray-600">Mind, body, and spirit integration through various healing modalities</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Leaf className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Organic Nutrition</h3>
              <p className="text-gray-600">Fresh, locally-sourced organic meals designed for optimal health</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-teal-500 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready for Your Wellness Journey?</h2>
          <p className="text-xl text-teal-100 mb-8">
            Join thousands of others who have transformed their lives through our wellness retreats
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Book Your Retreat Today
            </button>
            <button className="border border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-teal-600 transition-colors">
              Download Brochure
            </button>
          </div>
        </div>
      </div>

      {/* Modal for Retreat Details */}
      {selectedRetreat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="relative">
              <img src={selectedRetreat?.image} alt={selectedRetreat?.title} className="w-full h-48 object-cover rounded-t-2xl" />
              <button 
                onClick={() => setSelectedRetreat(null)}
                className="absolute top-4 right-4 bg-white rounded-full p-2 hover:bg-gray-100"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{selectedRetreat?.title}</h3>
              <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                <div className="flex items-center space-x-1">
                  <MapPin className="w-4 h-4" />
                  <span>{selectedRetreat.location}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>{selectedRetreat.duration}</span>
                </div>
              </div>
              
              <p className="text-gray-600 mb-6">{selectedRetreat.description}</p>
              
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">What's Included:</h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {selectedRetreat?.includes?.map((item: string, index: number) => (
                    <li key={index} className="flex items-center space-x-2 text-gray-600">
                      <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t">
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl font-bold text-teal-600">{selectedRetreat?.price}</span>
                    <span className="text-lg text-gray-400 line-through">{selectedRetreat?.originalPrice}</span>
                  </div>
                  <div className="text-sm text-gray-600">Per person</div>
                </div>
                <button className="bg-teal-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-600 transition-colors">
                  Book Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WellnessRetreats;